import { useEffect, useRef } from 'react';

/**
 * Focus management for the board/save family of HAND-ROLLED dialog sheets
 * (`ProductDetailSheet`, `SavePickerSheet`, `BoardSearchPill`,
 * `BoardPriceFilter`). These render their own portal + scrim and declare
 * `role="dialog"`, but the SDK primitive that would give focus behaviour for
 * free (vaul) is only used by the always-open saved tray — the triggered,
 * dismissible sheets are bespoke, so they never trapped focus, never moved
 * focus in on open, and never returned it to the opener on close (WCAG 2.1 AA
 * 2.4.3 Focus Order + 2.1.2 No Keyboard Trap, bug row 120).
 *
 * This hook is the shared fix. Point `containerRef` at the element that carries
 * `role="dialog"` and it will, while `open` is true:
 *   • move focus INTO the dialog on open (to `initialFocusRef` if given, else
 *     the container itself — which announces the dialog's accessible name);
 *   • TRAP Tab / Shift+Tab within the dialog, wrapping at both ends. The trap
 *     is container-scoped (the keydown listener lives on the dialog element),
 *     so it keeps focus cycling among the dialog's own focusables; it does NOT
 *     try to recapture focus that has fully left the container — the sheets
 *     close on scrim-click, so focus never escapes an open dialog to begin with;
 *   • RESTORE focus to whatever was focused before open on close/unmount.
 *
 * Escape is OPTIONAL (`onEscape`): the two popovers that lacked any Escape
 * handler (`BoardSearchPill`, `BoardPriceFilter`) get one from here, while the
 * sheets that already ship a carefully phased Escape handler (the PDP's
 * bubble-phase listener, the picker's capture-phase + stopPropagation listener
 * that must win over a PDP stacked beneath it) keep their own and pass no
 * `onEscape`. `escapeCapture` selects the phase for callers that DO delegate it.
 *
 * Deliberately does NOT own scroll-lock — the sheets already lock the document
 * body + the active inner scroller via their own effects (see
 * `useActiveScrollContainer`); duplicating it here would double-toggle the
 * overflow style.
 */

// Elements that can receive keyboard focus. Note: NO visibility filtering — a
// visually-hidden-but-focusable control (e.g. the mobile PDP's sr-only Close
// button) MUST stay in the trap, and jsdom reports every element as
// zero-size/`offsetParent === null` anyway, so a layout-based filter would
// empty the set under test.
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'audio[controls]',
  'video[controls]',
  '[contenteditable]:not([contenteditable="false"])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const getFocusable = (container: HTMLElement): HTMLElement[] =>
  Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (el) => el.getAttribute('aria-hidden') !== 'true' && !el.hasAttribute('inert'),
  );

export interface UseDialogA11yOptions {
  /** Whether the dialog is currently open/mounted. */
  open: boolean;
  /** Ref to the element carrying `role="dialog"`. Focus is trapped within it
   *  and returned to the opener on close. */
  containerRef: React.RefObject<HTMLElement | null>;
  /** Element to focus on open. Defaults to the container itself (made
   *  programmatically focusable with `tabindex=-1`), which announces the
   *  dialog's accessible name to a screen reader. */
  initialFocusRef?: React.RefObject<HTMLElement | null>;
  /** Restore focus to the pre-open `activeElement` on close. Default true. */
  restoreFocus?: boolean;
  /** When provided, Escape fires this. Sheets that already own an Escape
   *  handler omit it and keep theirs. */
  onEscape?: () => void;
  /** Bind the Escape listener in the capture phase (+ `stopPropagation`) so a
   *  sheet stacked over another Escape-listening layer closes only itself.
   *  Default false (bubble phase). */
  escapeCapture?: boolean;
}

export function useDialogA11y({
  open,
  containerRef,
  initialFocusRef,
  restoreFocus = true,
  onEscape,
  escapeCapture = false,
}: UseDialogA11yOptions): void {
  // Latch the escape callback so a new identity each render doesn't re-arm the
  // focus effect (which would re-focus the container on every render).
  const onEscapeRef = useRef(onEscape);
  useEffect(() => {
    onEscapeRef.current = onEscape;
  });

  // Latch `restoreFocus` the same "latest ref" way, but mutated DURING RENDER
  // (not in an effect): the focus-trap effect below is keyed on `open` alone,
  // so its cleanup closure is created once per open and normally reads
  // whatever `restoreFocus` was at that moment. A caller that flips this to
  // `false` mid-dialog — THE-1299's sign-in hand-off, which must not let this
  // dialog's unmount steal focus back out of a modal it just opened — needs
  // that flip picked up by a cleanup that was already scheduled. Mutating
  // during render (not via its own effect) guarantees the ref is current by
  // the time ANY effect's cleanup runs this commit, regardless of effect
  // declaration order.
  const restoreFocusRef = useRef(restoreFocus);
  restoreFocusRef.current = restoreFocus;

  // Focus move-in + trap + restore — keyed ONLY on `open` so it runs once per
  // open/close, never per render.
  useEffect(() => {
    if (!open) return undefined;
    const container = containerRef.current;
    if (!container) return undefined;

    const opener = (typeof document !== 'undefined'
      ? document.activeElement
      : null) as HTMLElement | null;

    // The container must be focusable for the name-announcing move-in.
    if (!container.hasAttribute('tabindex')) {
      container.setAttribute('tabindex', '-1');
    }

    const target = initialFocusRef?.current ?? container;
    target.focus({ preventScroll: true });

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const focusables = getFocusable(container);
      if (focusables.length === 0) {
        // Nothing to tab to — keep focus on the dialog rather than letting it
        // escape to the page behind the scrim.
        e.preventDefault();
        container.focus({ preventScroll: true });
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (e.shiftKey) {
        if (active === first || active === container) {
          e.preventDefault();
          last.focus();
        }
      } else if (active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    container.addEventListener('keydown', onKeyDown);

    return () => {
      container.removeEventListener('keydown', onKeyDown);
      if (
        restoreFocusRef.current &&
        opener &&
        opener.isConnected &&
        typeof opener.focus === 'function'
      ) {
        opener.focus({ preventScroll: true });
      }
    };
    // containerRef / initialFocusRef are stable refs; restoreFocus is read at
    // cleanup. Re-running on `open` alone is the intent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Optional Escape-to-close.
  useEffect(() => {
    if (!open || typeof window === 'undefined') return undefined;
    if (!onEscapeRef.current) return undefined;
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (escapeCapture) e.stopPropagation();
      onEscapeRef.current?.();
    };
    window.addEventListener('keydown', handler, { capture: escapeCapture });
    return () =>
      window.removeEventListener('keydown', handler, { capture: escapeCapture });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, escapeCapture]);
}

export default useDialogA11y;
